<script lang="ts" setup>
import { computed, ref } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppStandardModal } from '@libs/ui';
import type { Location } from '@libs/maps';

import type {
  AddFligthsModalRoute,
  AddFlightsModalInitialRoute,
} from './addFlightsModal.model';
import { useAddFlighstModalStore } from './store/addFlightsModalStore';
import AddFlightsView from './AddFlightsView.vue';

interface AddFlightsModalProps {
  routes: AddFlightsModalInitialRoute[];
  startPositon: Location | undefined;
  endPosition: Location | undefined;
}

const props = defineProps<AddFlightsModalProps>();

interface AddFlightsModalEmits {
  (eventName: 'close', context: void): void;
  (eventName: 'confirm', routes: AddFligthsModalRoute[]): void;
}

const emit = defineEmits<AddFlightsModalEmits>();

const { t } = i18n.global;

const addFlightsModalStore = useAddFlighstModalStore();

const modalTitle = computed(() => {
  if (addFlightsModalStore.isEditing) {
    return addFlightsModalStore.isEditingFrom
      ? t('modals.addFlights.departureAirport')
      : t('modals.addFlights.arrivalAirport');
  }

  return t('modals.addFlights.title');
});

const buttonLabel = computed(() =>
  addFlightsModalStore.isEditing
    ? t('modals.addFlights.buttons.done')
    : t('modals.addFlights.buttons.addFlights')
);

const modalSubtitle = ref<string | undefined>(undefined);

function handleClose(): void {
  if (addFlightsModalStore.isEditing) {
    closeAirportSelection();
    return;
  }
  emit('close');
}

function handleConfirm(): void {
  if (addFlightsModalStore.isEditing) {
    closeAirportSelection();
    return;
  }
  emit('confirm', addFlightsModalStore.routes);
}

function closeAirportSelection(): void {
  addFlightsModalStore.clearEditingAirport();
  modalSubtitle.value = undefined;
}
</script>

<template>
  <AppStandardModal
    :is-button-disabled="addFlightsModalStore.isButtonDisabled"
    @close="handleClose"
    @confirm="handleConfirm"
  >
    <template #title>{{ modalTitle }}</template>

    <template #subtitle>{{ modalSubtitle }}</template>

    <template #body>
      <AddFlightsView
        :routes="props.routes"
        :start-positon="props.startPositon"
        :end-position="props.endPosition"
      />
    </template>

    <template #button>{{ buttonLabel }}</template>
  </AppStandardModal>
</template>
